import {
  getMembershipPlanDisplayPricing,
  type MarketedMembershipPlan,
  type MarketedMembershipPlanType,
} from '@frontend/api-client';
import { convertDateLikeToDateISOString, dayjs, makeCent, match } from '@frontend/duck-tape';
import { useEffect, useRef, useState } from '@frontend/react';
import {
  membershipInitialState,
  membershipStateMachine,
  MembershipWizardStep,
  useDefaultPromoCode,
  usePromotionTranslation,
  type MembershipWizardState,
  type PromotionTranslationActions,
  type PromotionTranslationState,
} from '@frontend/react-domain';
import { OnboardingCard, StripeProvider } from '@frontend/web/components';
import {
  getValidatedPromoInput,
  useAnalytics,
  useLazyRetrievePromotionDetailsQuery,
  useMembershipPlans,
  useRetrieveClientQuery,
} from '@frontend/web/hooks';
import { handleMutation } from '@frontend/web/utils';
import { useNavigate } from '@tanstack/react-router';
import { useReducer } from 'react';
import type { PaymentCaptureRef } from '../../PaymentCapture';
import { MembershipWizardFooter } from './MembershipWizardFooter';
import { PaymentCaptureStep } from './PaymentCaptureStep';
import { SelectPlanStep } from './SelectPlanStep';

export type MembershipWizardProps = {
  overridePromoCode?: string;
};

type MembershipWizardContentProps = {
  dispatch: React.Dispatch<Parameters<typeof membershipStateMachine>[1]>;
  displayPlanLookup: ReturnType<typeof useMembershipPlans>['displayPlanLookup'];
  displaySelectablePlans: ReturnType<typeof useMembershipPlans>['displaySelectablePlans'];
  isError: boolean;
  isLoading: boolean;
  onRefetch: () => void;
  pricingState: ReturnType<typeof useMembershipPlans>['pricingState'];
  promoActions: PromotionTranslationActions;
  promoState: PromotionTranslationState;
  state: MembershipWizardState;
};

const MembershipWizardContent = ({
  dispatch,
  displayPlanLookup,
  displaySelectablePlans,
  isError,
  isLoading,
  onRefetch,
  pricingState,
  promoActions,
  promoState,
  state,
}: MembershipWizardContentProps) => {
  const { track } = useAnalytics();
  const navigate = useNavigate();
  const onboardingSessionIdRef = useRef(`${Date.now()}`);
  const paymentCaptureRef = useRef<PaymentCaptureRef>(null);
  const previousStepRef = useRef<MembershipWizardStep | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onboardingSessionId = onboardingSessionIdRef.current;

  const selectedPlanData = state.selectedPlanType
    ? (displayPlanLookup[state.selectedPlanType] as MarketedMembershipPlan | undefined)
    : undefined;
  const selectedPlanPricing = getMembershipPlanDisplayPricing(selectedPlanData);
  const shouldShowPromotionCopy = Boolean(selectedPlanPricing?.secondaryPrice);

  const [nextBillingDate] = useState(() => convertDateLikeToDateISOString(dayjs().add(1, 'month')));

  const amountCharged = selectedPlanPricing?.primaryPrice ?? makeCent(0);

  useEffect(() => {
    if (previousStepRef.current === state.step) {
      return;
    }

    // eslint-disable-next-line fp/no-mutation -- Ref mutation is required to detect step transitions
    previousStepRef.current = state.step;

    track({
      data: {
        onboardingSessionId,
        planCode: selectedPlanData?.planCode,
        platform: 'web',
        purchaseType: state.selectedPlanType ? 'Subscription' : undefined,
        step: state.step,
      },
      event: 'membershipStepViewed',
      topic: 'onboarding',
    });
  }, [onboardingSessionId, selectedPlanData?.planCode, state.selectedPlanType, state.step, track]);

  const handleApplyPromoCode = async (promoCode: string) => {
    const promotionDetails = await promoActions.handleApplyPromoCode(promoCode);

    if (promotionDetails) {
      track({
        data: {
          onboardingSessionId,
          planCode: selectedPlanData?.planCode,
          platform: 'web',
          purchaseType: 'Subscription',
          step: 'selectPlan',
        },
        event: 'membershipPromoCodeApplied',
        topic: 'onboarding',
      });

      return promotionDetails;
    }

    track({
      data: {
        errorCategory: 'validation',
        errorCode: 'ONBOARDING_MEMBERSHIP_PROMO_APPLY_FAILED',
        errorSource: 'promoApply',
        onboardingSessionId,
        planCode: selectedPlanData?.planCode,
        platform: 'web',
        purchaseType: 'Subscription',
        retriable: true,
        step: 'selectPlan',
      },
      event: 'membershipPromoCodeSubmitFailed',
      topic: 'onboarding',
    });

    return promotionDetails;
  };

  const handleSelectPlan = (planType: MarketedMembershipPlanType) => {
    dispatch({ planType, type: 'selectPlan' });

    track({
      data: {
        onboardingSessionId,
        planCode: displayPlanLookup[planType]?.planCode,
        platform: 'web',
        purchaseType: 'Subscription',
        step: 'selectPlan',
      },
      event: 'membershipPlanSelected',
      topic: 'onboarding',
    });
  };

  const handleDismissPromo = () => {
    track({
      data: {
        onboardingSessionId,
        planCode: selectedPlanData?.planCode,
        platform: 'web',
        purchaseType: 'Subscription',
        step: 'selectPlan',
      },
      event: 'membershipPromoCodeRemoved',
      topic: 'onboarding',
    });

    promoActions.resetPromoCode();
    dispatch({ type: 'dismissPromo' });
  };

  const handleContinue = () => {
    match(state.step)
      .with(MembershipWizardStep.selectPlan, () => {
        if (state.selectedPlanType) {
          track({
            data: {
              onboardingSessionId,
              planCode: selectedPlanData?.planCode,
              platform: 'web',
              purchaseType: 'Subscription',
              step: 'selectPlan',
            },
            event: 'membershipContinueToPaymentSelected',
            topic: 'onboarding',
          });
        }

        dispatch({ type: 'continueToPayment' });
      })
      .with(MembershipWizardStep.paymentCapture, () => {
        setIsSubmitting(true);
        paymentCaptureRef.current?.submitPayment();
      })
      .otherwise(() => undefined);
  };

  const handleBack = () => {
    dispatch({ type: 'back' });
  };

  const handlePaymentError = (error: string) => {
    setIsSubmitting(false);
    dispatch({ error, type: 'paymentError' });
  };

  const handlePaymentSuccess = (data: { amountCharged: Cent; nextBillingDate: DateISOString }) => {
    setIsSubmitting(false);
    dispatch({ type: 'paymentSuccess' });
    navigate({
      state: {
        onboardingSessionId,
        paidToday: data.amountCharged,
        planCode: selectedPlanData?.planCode,
        promotionId: promoState.promotionDetails?.promotionId,
        promotionName: shouldShowPromotionCopy ? promoState.promotionName : undefined,
        purchaseType: 'Subscription',
        renewalDate: data.nextBillingDate,
        renewalPlanLabel: selectedPlanData?.label,
        step: 'complete',
      } as Record<string, unknown>,
      to: '/onboarding/complete',
    });
  };

  const isFooterLoading = isSubmitting;

  const stepContent = match(state.step)
    .with(MembershipWizardStep.selectPlan, () => (
      <SelectPlanStep
        appliedPromoCode={promoState.promoCode}
        hasSuccessfullyAppliedPromoCode={promoState.hasSuccessfullyAppliedPromoCode}
        isApplyPromoCodeLoading={promoState.isApplyPromoCodeLoading}
        isError={isError}
        isLoading={isLoading}
        onApplyPromoCode={handleApplyPromoCode}
        onDismissPromo={handleDismissPromo}
        onRefetch={onRefetch}
        onSelectPlan={handleSelectPlan}
        pricingState={pricingState}
        promoCodeErrorMessage={promoState.promoCodeErrorMessage}
        promotionDescription={promoState.promotionDescription}
        promotionName={promoState.promotionName}
        selectablePlans={displaySelectablePlans}
        selectedPlanType={state.selectedPlanType}
      />
    ))
    .with(MembershipWizardStep.paymentCapture, () =>
      selectedPlanData ? (
        <PaymentCaptureStep
          amountCharged={amountCharged}
          nextBillingDate={nextBillingDate}
          onError={handlePaymentError}
          onSuccess={handlePaymentSuccess}
          onboardingSessionId={onboardingSessionId}
          paymentCaptureRef={paymentCaptureRef}
          paymentError={state.paymentError}
          planCode={selectedPlanData.planCode}
          promoCode={promoState.promoCode}
          promotionDescription={shouldShowPromotionCopy ? promoState.promotionDescription : undefined}
          promotionDetails={shouldShowPromotionCopy ? promoState.promotionDetails : undefined}
          promotionName={shouldShowPromotionCopy ? promoState.promotionName : undefined}
          selectedPlanData={selectedPlanData}
          step="paymentCapture"
        />
      ) : null,
    )
    .otherwise(() => null);

  const isCheckoutPage = state.step === MembershipWizardStep.paymentCapture;
  const isPricingTransitioning = pricingState === 'transitioning';

  const isFooterDisabled =
    state.step === MembershipWizardStep.selectPlan ? !selectedPlanData || isPricingTransitioning : isSubmitting;
  const wizardBody = (
    <>
      {stepContent}
      <MembershipWizardFooter
        amountDueToday={amountCharged}
        isDisabled={isFooterDisabled}
        isLoading={isFooterLoading}
        isUpdatingPrices={isPricingTransitioning}
        onContinue={handleContinue}
        step={state.step}
      />
    </>
  );

  return (
    <OnboardingCard
      onBack={isCheckoutPage ? handleBack : undefined}
      title={isCheckoutPage ? 'Your subscription' : 'Select your Membership'}
    >
      {isCheckoutPage ? <StripeProvider>{wizardBody}</StripeProvider> : wizardBody}
    </OnboardingCard>
  );
};

export const MembershipWizard = ({ overridePromoCode }: MembershipWizardProps) => {
  const [state, dispatch] = useReducer(membershipStateMachine, membershipInitialState);
  const retrievePromotionDetailsHookResult = useLazyRetrievePromotionDetailsQuery();
  const retrieveClientHookResult = useRetrieveClientQuery();

  const defaultPromoCode = useDefaultPromoCode({
    overridePromoCode,
    retrieveClientHookResult,
  });

  const { actions: promoActions, state: promoState } = usePromotionTranslation({
    defaultPromoCode,
    firstPurchaseData: undefined,
    handleMutation,
    retrievePromotionDetailsHookResult,
  });

  const validatedPromo = getValidatedPromoInput({
    promoCode: promoState.promoCode,
    promotionId: promoState.promotionDetails?.promotionId,
  });

  const { displayPlanLookup, displaySelectablePlans, isError, isLoading, pricingState, refetch } = useMembershipPlans({
    validatedPromo,
  });

  useEffect(() => {
    if (promoState.hasSuccessfullyAppliedPromoCode && promoState.promotionDetails && promoState.promoCode) {
      dispatch({
        promoCode: promoState.promoCode,
        promotionDetails: promoState.promotionDetails,
        type: 'applyPromo',
      });
    }
  }, [promoState.hasSuccessfullyAppliedPromoCode, promoState.promotionDetails, promoState.promoCode]);

  return (
    <MembershipWizardContent
      dispatch={dispatch}
      displayPlanLookup={displayPlanLookup}
      displaySelectablePlans={displaySelectablePlans}
      isError={isError}
      isLoading={isLoading}
      onRefetch={refetch}
      pricingState={pricingState}
      promoActions={promoActions}
      promoState={promoState}
      state={state}
    />
  );
};
